import React, { useContext, createContext } from 'react';
import PropTypes from 'prop-types';

const MediaContext = createContext();

function useMedia() {
  const context = useContext(MediaContext);

  if (!context) {
    throw new Error('useMedia must be used within MediaProvider');
  }

  return context;
}

const propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  breakpoints: PropTypes.objectOf(PropTypes.string),
};

const defaultProps = {
  breakpoints: {
    small: '(min-width: 768px)',
    medium: '(min-width: 920px)',
    large: '(min-width: 1200px)',
  },
};

function areArraysEqual(array1, array2) {
  return array1.length === array2.length
    && array1.every((value, index) => value === array2[index]);
}

class MediaProvider extends React.Component {
  constructor(props) {
    super(props);
    this.state = { matches: [] };
    this.mediaItems = null;
    this.updateMatches = this.updateMatches.bind(this);
  }

  componentDidMount() {
    this.setup();
  }

  componentDidUpdate(prevProps) {
    const { breakpoints } = this.props;
    if (breakpoints !== prevProps.breakpoints) {
      this.tearDown();
      this.setup();
    }
  }

  componentWillUnmount() {
    this.tearDown();
  }

  setup() {
    const { breakpoints } = this.props;

    this.mediaItems = Object.keys(breakpoints).map(name => (
      { name, media: window.matchMedia(breakpoints[name]) }
    ));

    this.mediaItems.forEach(({ media }) => media.addListener(this.updateMatches));
    this.updateMatches();
  }

  tearDown() {
    this.mediaItems.forEach(({ media }) => media.removeListener(this.updateMatches));
    this.mediaItems = null;
  }

  updateMatches() {
    const { matches } = this.state;
    const newMatches = this.mediaItems
      .filter(({ media }) => media.matches)
      .map(({ name }) => name);

    if (!areArraysEqual(newMatches, matches)) {
      this.setState({ matches: newMatches });
    }
  }

  render() {
    const { children } = this.props;
    const { matches } = this.state;

    return (
      <MediaContext.Provider value={matches}>
        {children}
      </MediaContext.Provider>
    );
  }
}

MediaProvider.propTypes = propTypes;
MediaProvider.defaultProps = defaultProps;

export { MediaProvider, useMedia };
